import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Icon } from 'antd';
import queryString from 'query-string';

import CongratsImg from '../../../assets/images/space-listing/congratulations.svg';
import { getProfileData, setProfileData } from '../Common/DataController';
import { spaceListingData } from '../Common/ProfileListingDataModal';
import { store } from '../../../index';
import { syncWithCalendars, getEmail, calendarAuthData } from '../ListingAction';
import { COUNTRY_CODES } from '../../Common/constants';
import { SPACE_AND_NOTICE, SEATING_AND_COMMON, SPACE_LOCATION } from '../SpaceListingConstants';

const resolveCountryPath = (routeCountry, locationMetaCountry, listingCountryCode) => {
  if (routeCountry && routeCountry !== 'all') {
    return routeCountry;
  }

  if (locationMetaCountry) {
    return locationMetaCountry;
  }

  const matchedCountry = Object.values(COUNTRY_CODES).find(
    (details) =>
      details.country_shortCode === listingCountryCode ||
      details.country_longCode === listingCountryCode ||
      details.country_path === listingCountryCode
  );

  if (matchedCountry?.country_path) {
    return matchedCountry.country_path;
  }

  return COUNTRY_CODES.SRI_LANKA.country_path;
};

const Congratulations = () => {
  const { country } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const response = useSelector((state) => state?.spaceListing?.submitResponse || {});
  const spaceId = useSelector((state) => state?.spaceListing?.submitResponse?.id || null);
  const fetching = useSelector((state) => state?.spaceListing?.fetching);
  const googleAccessToken = useSelector((state) => state?.spaceListing?.tokenObj?.access_token || null);
  const email = useSelector((state) => state?.spaceListing?.authEmail?.email || null);
  const googleRefreshToken = useSelector((state) => state?.spaceListing?.tokenObj?.refresh_token || null);
  const googleStatus = useSelector((state) => state?.spaceListing?.tokenObj?.googleStatus || 0);
  const countryLongName = useSelector((state) => state?.locationMeta?.data?.country_longCode?.replace(' ', '-') || null);
  const outlookInfo = useSelector((state) => state?.spaceListing?.data || {});
  const outlookStatus = useSelector((state) => state?.spaceListing?.outlookStatus || 0);

  // Capture listing values before congratulations resets session profile data.
  const listingSnapshot = useMemo(() => {
    const profileData = getProfileData() || {};
    return {
      eventTypeId: profileData?.[SPACE_AND_NOTICE]?.[0]?.activity?.[0]?.id || null,
      unit: profileData?.[SEATING_AND_COMMON]?.[0]?.pricingOptions?.[0] || '',
      listingCountryCode:
        profileData?.[SPACE_LOCATION]?.[0]?.countryCode ||
        profileData?.[SPACE_LOCATION]?.[0]?.spaceCountry ||
        null,
    };
  }, []);

  const [unit, setUnit] = useState(listingSnapshot.unit);
  const countryPath = resolveCountryPath(country, countryLongName, listingSnapshot.listingCountryCode);

  const googleInfo = {
    refreshToken: googleRefreshToken,
    accessToken: googleAccessToken,
    email: email,
  };

  function isJson(str) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }

  useEffect(() => {
    let storage = {};
    let refreshToken = null;
    let accessToken = null;

    Object.keys(sessionStorage).forEach((key) => {
      storage[key] = sessionStorage.getItem(key);

      if (storage && storage[key] && storage[key] && isJson(storage[key])) {
        if (JSON.parse(storage[key]).credentialType == 'AccessToken') {
          accessToken = JSON.parse(storage[key]).secret;
        } else if (JSON.parse(storage[key]).credentialType == 'RefreshToken') {
          refreshToken = JSON.parse(storage[key]).secret;
        }

        const outlookAuthInfo = {
          refreshToken,
          accessToken,
          email,
        };

        store.dispatch(calendarAuthData(outlookAuthInfo));
      }
    });
  }, [sessionStorage]);

  useEffect(() => {
    if (!unit) {
      setUnit(getProfileData()?.[SEATING_AND_COMMON]?.[0]?.pricingOptions?.[0] || '');
    }
  }, []);

  useEffect(() => {
    if (googleAccessToken) {
      dispatch(getEmail(googleAccessToken));
    }
  }, [googleAccessToken]);

  useEffect(() => {
    const data = {
      spaceId: spaceId,
      googleStatus: googleStatus,
      outlookStatus: outlookStatus,
      googleInfo,
      outlookInfo,
    };

    if (spaceId && (googleStatus === 1 || outlookStatus === 1)) {
      dispatch(syncWithCalendars(data));
    }
  }, [spaceId]);

  useEffect(() => {
    if (unit) {
      setProfileData(spaceListingData(countryPath));
    }
  }, [unit, countryPath]);

  const getEventTypeId = () => {
    if (response?.eventTypes?.[0]?.id) return response.eventTypes[0].id;
    if (response?.events?.[0]?.eventType?.id) return response.events[0].eventType.id;
    if (response?.events?.[0]?.eventTypeId) return response.events[0].eventTypeId;
    if (listingSnapshot.eventTypeId) return listingSnapshot.eventTypeId;
    return null;
  };

  const handleViewSpace = () => {
    const eventTypeId = getEventTypeId();
    const resolvedSpaceId = spaceId || response?.id;

    if (!resolvedSpaceId || !eventTypeId) {
      return;
    }

    const query = queryString.stringify({
      eventTypeId,
      spaceId: resolvedSpaceId,
      ...(unit ? { unit } : {}),
    });

    history.push(`/${countryPath}/space/bookings?${query}`);
  };

  const canViewSpace = Boolean(spaceId && getEventTypeId() && !fetching);

  return (
    <>
      <div className="left-side-inner-wrapper congrats-screen">
        <h1 className="listing-page-title">Congratulations!</h1>
        <div className="content-wrapper">
          <div className="paragraph-wrapper">
            <p>You have successfully listed your space on MillionSpaces!</p>
            <p>Our team will review your listing and notify you when your space is available to be booked on the MillionSpaces website.</p>
            <p>
              {' '}
              A confirmation has been sent to your email address. <span className="text">(it may sometimes land in your SPAM folder)</span>
            </p>
          </div>
          <img className="congrats-img" src={CongratsImg} alt="congratulations" />
          <div className="btn-wrapper">
            {!canViewSpace ? (
              <div className="btn border-btn disabled-btn">
                <Icon type="loading" className="loading-spinner" /> loading...
              </div>
            ) : (
              <div className="btn border-btn" onClick={handleViewSpace}>
                view space
              </div>
            )}
            <div className="btn yellow-btn" onClick={() => history.push(`/${countryPath}`)}>
              go to home
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Congratulations;
